import { ethers } from 'ethers';
import type {
  CryptoAssetBalanceParams,
  CryptoAssetBalanceResult,
  CryptoAssetEstimateGasParams,
  CryptoAssetGasEstimate,
  CryptoAssetMaxSendAmountParams,
  CryptoAssetMaxSendAmountResult,
  CryptoAssetTransactionHistoryResult,
  CryptoAssetTransactionHistoryParams,
  CryptoAssetSendParams,
  CryptoAssetTransactionResult,
  ICryptoAssetProvider,
} from './ICryptoAssetProvider';
import type { CryptoAsset } from '@/types/WalletTypes';
import {
  bufferGasValue,
  createEthersRpcProviderResolver,
  getExplorerTransactionHistory,
  getSameFeeAssetMaxSendAmount,
  getStandardMaxSendAmount,
  requireCryptoAssetChainId,
  requireMnemonic,
} from './shared';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
];
const ERC20_MIN_GAS_LIMIT = ethers.BigNumber.from(70000);
const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';
const MULTICALL_ABI = [
  'function aggregate(tuple(address target, bytes callData)[] calls) view returns (uint256 blockNumber, bytes[] returnData)',
];

export class EvmNativeCryptoAssetProvider implements ICryptoAssetProvider {
  readonly name: string = 'evm-native';

  protected readonly getRpcProvider = createEthersRpcProviderResolver();

  supports(cryptoAsset: CryptoAsset): boolean {
    return cryptoAsset.traits.isEvm && cryptoAsset.traits.isNative;
  }

  validateAddress(address: string): boolean {
    return ethers.utils.isAddress(address);
  }

  getTransactionHistory(params: CryptoAssetTransactionHistoryParams): Promise<CryptoAssetTransactionHistoryResult> {
    return getExplorerTransactionHistory({
      ...params,
      action: 'txlist',
      filterTransaction: (txn) => txn.value !== '0' && (!txn.input || txn.input === '0x'),
    });
  }

  async getBalance({ address, cryptoAsset }: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    try {
      const balance = await rpcProvider.getBalance(address);
      return {
        balance: ethers.utils.formatUnits(balance, cryptoAsset.display.decimalPrecision),
        canSendFunds: true,
        hasError: false,
      };
    } catch {
      return { balance: null, canSendFunds: false, hasError: true };
    }
  }

  async estimateGas({ amount, cryptoAsset, from, to }: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const requestedValue = ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const { fee } = await getEvmGasQuote({
      estimatedGasLimit: await rpcProvider.estimateGas({ from, to, value: requestedValue }),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });

    return {
      coin: ethers.utils.formatUnits(fee, cryptoAsset.display.decimalPrecision),
      usd: 0,
    };
  }

  getMaxSendAmount(params: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult {
    return getSameFeeAssetMaxSendAmount(params);
  }

  async send({
    amount,
    cryptoAsset,
    isMaxSend,
    mnemonic,
    privateKey,
    to,
  }: CryptoAssetSendParams): Promise<CryptoAssetTransactionResult> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const wallet = privateKey
      ? new ethers.Wallet(privateKey).connect(rpcProvider)
      : ethers.Wallet.fromMnemonic(requireMnemonic(mnemonic)).connect(rpcProvider);

    const txnContext = await getEvmTxnContext({
      provider: rpcProvider,
      wallet,
    });
    const requestedValue = ethers.utils.parseUnits(amount, cryptoAsset.display.decimalPrecision);
    const gasQuote = await getEvmGasQuote({
      estimatedGasLimit: await rpcProvider.estimateGas({ to, value: requestedValue }),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });
    const sendValue = await resolveNativeSendValue({
      fee: gasQuote.fee,
      isMaxSend,
      provider: rpcProvider,
      requestedValue,
      walletAddress: txnContext.connectedWallet.address,
    });

    return signAndSendNativeTxn({
      provider: rpcProvider,
      cryptoAsset,
      gasQuote,
      to,
      txnContext,
      sendValue,
    });
  }
}

export class EvmErc20CryptoAssetProvider implements ICryptoAssetProvider {
  readonly name: string = 'evm-erc20';

  protected readonly getRpcProvider = createEthersRpcProviderResolver();

  private readonly balanceAggregator = new Erc20BalanceAggregator((requests) =>
    this.getBalancesForAddressAndChain(requests)
  );

  supports(cryptoAsset: CryptoAsset): boolean {
    return cryptoAsset.traits.isEvm && cryptoAsset.traits.isErc20;
  }

  validateAddress(address: string): boolean {
    return ethers.utils.isAddress(address);
  }

  getTransactionHistory(params: CryptoAssetTransactionHistoryParams): Promise<CryptoAssetTransactionHistoryResult> {
    return getExplorerTransactionHistory({ ...params, action: 'tokentx' });
  }

  async getBalance({ address, cryptoAsset }: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    return this.balanceAggregator.getBalance({ address, cryptoAsset });
  }

  async estimateGas({ amount, cryptoAsset, from, to }: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const requestedTokenAmount = ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const contract = new ethers.Contract(cryptoAsset.asset.definitionAddress, ERC20_ABI, rpcProvider);
    const { fee } = await getEvmGasQuote({
      estimatedGasLimit: await contract.estimateGas.transfer(to, requestedTokenAmount, { from }),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });

    return {
      coin: ethers.utils.formatUnits(fee, cryptoAsset.fee.decimalPrecision),
      usd: 0,
    };
  }

  getMaxSendAmount(params: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult {
    return getStandardMaxSendAmount(params);
  }

  async send({
    amount,
    cryptoAsset,
    isMaxSend,
    mnemonic,
    privateKey,
    to,
  }: CryptoAssetSendParams): Promise<CryptoAssetTransactionResult> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const wallet = privateKey
      ? new ethers.Wallet(privateKey).connect(rpcProvider)
      : ethers.Wallet.fromMnemonic(requireMnemonic(mnemonic)).connect(rpcProvider);

    const txnContext = await getEvmTxnContext({
      provider: rpcProvider,
      wallet,
    });
    const contract = new ethers.Contract(cryptoAsset.asset.definitionAddress, ERC20_ABI, txnContext.connectedWallet);
    const sendTokenAmount = isMaxSend
      ? await contract.balanceOf(txnContext.connectedWallet.address)
      : ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const gasQuote = await getEvmGasQuote({
      estimatedGasLimit: await contract.estimateGas.transfer(to, sendTokenAmount),
      minimumGasLimit: ERC20_MIN_GAS_LIMIT,
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });

    const tx = await contract.transfer(to, sendTokenAmount, {
      nonce: txnContext.latestNonce,
      gasLimit: gasQuote.gasLimit,
      ...gasQuote.txnFeeFields,
    });
    const receipt = await tx.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      blockNumber: receipt.blockNumber,
    };
  }

  private async getBalanceDirect({
    address,
    cryptoAsset,
  }: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    try {
      const rpcProvider = this.getRpcProvider(cryptoAsset);
      const contract = new ethers.Contract(cryptoAsset.asset.definitionAddress, ERC20_ABI, rpcProvider);
      const balance = await contract.balanceOf(address);

      return {
        balance: ethers.utils.formatUnits(balance, cryptoAsset.display.decimalPrecision),
        canSendFunds: true,
        hasError: false,
      };
    } catch {
      return { balance: null, canSendFunds: false, hasError: true };
    }
  }

  private async getBalancesForAddressAndChain(
    requests: CryptoAssetBalanceParams[]
  ): Promise<CryptoAssetBalanceResult[]> {
    const address = requests[0].address;
    try {
      const rpcProvider = this.getRpcProvider(requests[0].cryptoAsset);
      const erc20Interface = new ethers.utils.Interface(ERC20_ABI);
      const multicall = new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL_ABI, rpcProvider);
      const calls = requests.map((request) => ({
        target: request.cryptoAsset.asset.definitionAddress,
        callData: erc20Interface.encodeFunctionData('balanceOf', [address]),
      }));
      const [, returnData] = await multicall.aggregate(calls);

      return requests.map((request, index) => {
        const [balance] = erc20Interface.decodeFunctionResult('balanceOf', returnData[index]);
        return {
          balance: ethers.utils.formatUnits(balance, request.cryptoAsset.display.decimalPrecision),
          canSendFunds: true,
          hasError: false,
        };
      });
    } catch {
      return Promise.all(requests.map((request) => this.getBalanceDirect(request)));
    }
  }
}

type QueuedErc20BalanceRequest = CryptoAssetBalanceParams & {
  resolve: (result: CryptoAssetBalanceResult) => void;
};

class Erc20BalanceAggregator {
  private readonly batches = new Map<string, QueuedErc20BalanceRequest[]>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly loadBalanceBatch: (requests: CryptoAssetBalanceParams[]) => Promise<CryptoAssetBalanceResult[]>
  ) {}

  getBalance(params: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    return new Promise((resolve) => {
      const key = getBalanceAggregationKey(params);
      const batch = this.batches.get(key) ?? [];
      batch.push({ ...params, resolve });
      this.batches.set(key, batch);

      if (!this.timers.has(key)) {
        this.timers.set(
          key,
          setTimeout(() => this.flush(key), 100)
        );
      }
    });
  }

  private async flush(key: string) {
    const batch = this.batches.get(key) ?? [];
    this.batches.delete(key);
    this.timers.delete(key);
    if (!batch.length) {
      return;
    }
    const fallbackResult = { balance: null, canSendFunds: false, hasError: true };
    try {
      const results = await this.loadBalanceBatch(batch);
      batch.forEach((request, index) => request.resolve(results[index] ?? fallbackResult));
    } catch {
      batch.forEach((request) => request.resolve(fallbackResult));
    }
  }
}

const getBalanceAggregationKey = ({ address, cryptoAsset }: CryptoAssetBalanceParams) =>
  `${address.toLowerCase()}:${cryptoAsset.asset.blockchainConfiguration.chainId}`;

export type EvmTxnContext = {
  connectedWallet: ethers.Wallet;
  latestNonce: number;
};

export const getEvmTxnContext = async ({
  provider,
  wallet,
}: {
  provider: ethers.providers.Provider;
  wallet: ethers.Wallet;
}): Promise<EvmTxnContext> => {
  if (!wallet?.address) {
    throw Object.assign(new Error('Failed to send transaction'), { ErrorCode: 60001 });
  }
  const connectedWallet = wallet.connect(provider);
  const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');

  return {
    connectedWallet,
    latestNonce,
  };
};

export const getGasPricing = (feeData: ethers.providers.FeeData, usesEIP1559: boolean) => {
  const legacyGasPrice = feeData.gasPrice ?? feeData.maxFeePerGas ?? ethers.BigNumber.from(0);
  const maxFeePerGas = feeData.maxFeePerGas ?? legacyGasPrice;
  const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas ?? ethers.utils.parseUnits('1', 'gwei');
  const gasPrice = usesEIP1559 ? maxFeePerGas : legacyGasPrice;
  const txnFeeFields = usesEIP1559
    ? {
        maxFeePerGas,
        maxPriorityFeePerGas,
        type: 2,
      }
    : {
        gasPrice,
      };

  return {
    gasPrice,
    txnFeeFields,
  };
};

export type EvmGasQuote = ReturnType<typeof getGasPricing> & {
  fee: ethers.BigNumber;
  gasLimit: ethers.BigNumber;
};

export const getEvmGasQuote = async ({
  estimatedGasLimit,
  minimumGasLimit,
  provider,
  usesEIP1559,
}: {
  estimatedGasLimit: ethers.BigNumber;
  minimumGasLimit?: ethers.BigNumber;
  provider: ethers.providers.Provider;
  usesEIP1559: boolean;
}): Promise<EvmGasQuote> => {
  const pricing = getGasPricing(await provider.getFeeData(), usesEIP1559);
  let gasLimit = bufferGasValue(estimatedGasLimit);

  if (minimumGasLimit && gasLimit.lt(minimumGasLimit)) {
    gasLimit = minimumGasLimit;
  }

  return {
    ...pricing,
    gasLimit,
    fee: gasLimit.mul(pricing.gasPrice),
  };
};

export const resolveNativeSendValue = async ({
  fee,
  isMaxSend,
  provider,
  requestedValue,
  walletAddress,
}: {
  fee: ethers.BigNumber;
  isMaxSend: boolean;
  provider: ethers.providers.Provider;
  requestedValue: ethers.BigNumber;
  walletAddress: string;
}) => {
  if (!isMaxSend) {
    return requestedValue;
  }

  const balance = await provider.getBalance(walletAddress);
  const maxSendValue = balance.sub(fee);

  if (maxSendValue.lte(0)) {
    throw Object.assign(new Error('Insufficient funds to cover gas fees'), { ErrorCode: 60003 });
  }

  return maxSendValue;
};

export const signAndSendNativeTxn = async ({
  provider,
  cryptoAsset,
  gasQuote,
  sendValue,
  to,
  txnContext,
}: {
  provider: ethers.providers.Provider;
  cryptoAsset: CryptoAsset;
  gasQuote: EvmGasQuote;
  sendValue: ethers.BigNumberish;
  to: string;
  txnContext: EvmTxnContext;
}) => {
  const chainId = requireCryptoAssetChainId(cryptoAsset);
  const signedTx = await txnContext.connectedWallet.signTransaction({
    to,
    value: sendValue,
    nonce: txnContext.latestNonce,
    gasLimit: gasQuote.gasLimit,
    chainId,
    ...gasQuote.txnFeeFields,
  });

  return provider.sendTransaction(signedTx);
};
